/**
 * The last known signed-in user, remembered across reloads.
 *
 * Two things read this: the auth provider, so a reload with no connection does
 * not throw the cashier back to a login screen they cannot complete; and the
 * transport, so a request that arrives before React has finished wiring itself
 * up still knows whose permissions to apply.
 *
 * **Not a security boundary.** Permissions held on a device the operator
 * controls are a guardrail for the interface, not a guarantee — anyone who can
 * edit local storage can edit these. The real boundary is what the server
 * agrees to send a device: see the snapshot endpoint, which withholds costs
 * from accounts without `reports.margin`.
 */

import type { AuthenticatedUser } from '@bakery/shared';

const USER_KEY = 'bakery.user';

export function cacheUser(user: AuthenticatedUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* private window: the session simply will not survive a reload */
  }
}

export function cachedUser(): AuthenticatedUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthenticatedUser) : null;
  } catch {
    return null;
  }
}

export function clearCachedUser(): void {
  try {
    localStorage.removeItem(USER_KEY);
  } catch {
    /* nothing useful to do */
  }
}
